import * as Application from 'expo-application';
import { Linking, Platform } from 'react-native';
import { MMKV } from 'react-native-mmkv';
import { APP_CONSTANTS } from '@/config/constants';
import { globalState } from '@/state/globalState';
import { getStableRecoveryDeviceId } from '@/services/recovery/RecoveryRuntimeGate';
import { supabase } from '@/services/supabase';
import { addSentryBreadcrumb, captureSentryExceptionWithScope } from '@/services/monitoring/sentry';
import { recordBufferedLogEvent } from '@/utils/logBuffer';
import { getLogger } from '@/utils/logger';
import { getRuntimeClientBuild, parseClientBuild } from './clientBuild';
import { reportClientInstallationPresence } from './clientInstallationPresence';

export type AppUpdatePromptMode = 'optional' | 'required';
export type AppUpdatePromptPlatform = 'web' | 'android' | 'ios';
export type AppUpdatePromptAction = 'shown' | 'snoozed' | 'store_opened' | 'web_refresh' | 'completed';

export type AppUpdatePrompt = {
  campaignId: string;
  mode: AppUpdatePromptMode;
  platform: AppUpdatePromptPlatform;
  targetBuild: number;
  targetVersion: string | null;
  title: string;
  message: string;
  storeUrl: string | null;
  webUrl: string | null;
  expiresAt: string | null;
  shownCount: number;
  snoozeCount: number;
  snoozeUntil: string | null;
  completedAt: string | null;
};

type RawUpdatePrompt = {
  campaign_id?: unknown;
  mode?: unknown;
  platform?: unknown;
  target_build?: unknown;
  target_version?: unknown;
  title?: unknown;
  message?: unknown;
  store_url?: unknown;
  web_url?: unknown;
  expires_at?: unknown;
  shown_count?: unknown;
  snooze_count?: unknown;
  snooze_until?: unknown;
  completed_at?: unknown;
};

type StoredPrompt = {
  scopeKey: string;
  campaignId: string;
  snoozeUntil: string | null;
  pendingCompletion?: AppUpdatePrompt | null;
};

type UpdatePromptSnapshot = {
  prompt: AppUpdatePrompt | null;
  isChecking: boolean;
  lastError: string | null;
};

type UpdatePromptRpcError = {
  code?: unknown;
  message?: unknown;
  details?: unknown;
  hint?: unknown;
};

type UpdatePromptErrorDiagnostic = {
  error_code: string | null;
  error_message: string;
  error_details: string | null;
  error_hint: string | null;
};

const logger = getLogger('APP-(APP)-LAYOUT');
const storage = new MMKV({ id: 'app-guincho-update-prompts-v1' });
const fallbackStorage = new Map<string, string>();
const STORAGE_KEY = 'active';
const CHECK_THROTTLE_MS = 15 * 60 * 1000;
const OPTIONAL_SNOOZE_MS = 24 * 60 * 60 * 1000;
const WEB_REFRESH_SNOOZE_MS = 30 * 60 * 1000;
const EVENT_FLUSH_BUDGET_MS = 750;
const ERROR_DIAGNOSTIC_TEXT_LIMIT = 500;
const CONNECTIVITY_ERROR_HAYSTACK_DEPTH = 3;
const UPDATE_PROMPT_CONNECTIVITY_HINTS = [
  'unknownhostexception',
  'unable to resolve host',
  'no address associated with hostname',
  'failed to fetch',
  'network request failed',
  'networkerror',
  'network error',
  'enotfound',
  'eai_again',
  'etimedout',
  'econnreset',
  'econnrefused',
  'ehostunreach',
  'enetunreach',
  'err_name_not_resolved',
  'err_internet_disconnected',
  'err_network_changed',
  'err_connection_refused',
  'err_connection_reset',
  'err_connection_timed_out',
  'the internet connection appears to be offline',
  'the network connection was lost',
  'sockettimeout',
  'offline',
];

let snapshot: UpdatePromptSnapshot = { prompt: null, isChecking: false, lastError: null };
let lastCheckedAt: number | null = null;
let lastScopeKey: string | null = null;
let lastCheckedBuild: number | null = null;
const promptScopes = new WeakMap<AppUpdatePrompt, string>();

export const getAppUpdateCampaignBuild = (): number | null =>
  Platform.OS === 'web'
    ? parseClientBuild(getRuntimeClientBuild())
    : parseClientBuild(Application.nativeBuildVersion);
let checkQueue: Promise<AppUpdatePrompt | null> = Promise.resolve(null);
const listeners = new Set<(next: UpdatePromptSnapshot) => void>();

const emit = (next: Partial<UpdatePromptSnapshot>): void => {
  snapshot = { ...snapshot, ...next };
  listeners.forEach(listener => listener(snapshot));
};

const normalizePlatform = (): AppUpdatePromptPlatform | null => {
  if (Platform.OS === 'web' || Platform.OS === 'android' || Platform.OS === 'ios') {
    return Platform.OS;
  }
  return null;
};

const getStorageScopeKey = (): string | null => {
  const user = globalState.user.user.get();
  const profile = globalState.user.profile.get();
  const platform = normalizePlatform();
  if (!user?.id || profile?.id !== user.id || !profile?.empresa_id
    || globalState.company.data.get()?.id !== profile.empresa_id || !platform) return null;
  return `${profile.empresa_id}:${user.id}:${getStableRecoveryDeviceId()}:${platform}`;
};

const parseStoredPrompt = (value?: string): StoredPrompt | null => {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value) as Partial<StoredPrompt>;
    if (typeof parsed.scopeKey !== 'string' || typeof parsed.campaignId !== 'string') return null;
    return {
      scopeKey: parsed.scopeKey,
      campaignId: parsed.campaignId,
      snoozeUntil: typeof parsed.snoozeUntil === 'string' ? parsed.snoozeUntil : null,
      pendingCompletion: parsed.pendingCompletion
        && parsed.pendingCompletion.campaignId === parsed.campaignId
        && (parsed.pendingCompletion.mode === 'required' || parsed.pendingCompletion.mode === 'optional')
        && typeof parsed.pendingCompletion.title === 'string'
        && typeof parsed.pendingCompletion.message === 'string'
        && parseClientBuild(parsed.pendingCompletion.targetBuild) !== null
        && ['web', 'android', 'ios'].includes(parsed.pendingCompletion.platform)
        ? parsed.pendingCompletion : null,
    };
  } catch {
    return null;
  }
};

const getStoredString = (key: string): string | undefined => {
  if (typeof storage.getString === 'function') {
    return storage.getString(key);
  }
  return fallbackStorage.get(key);
};

const setStoredString = (key: string, value: string): void => {
  if (typeof storage.set === 'function') {
    storage.set(key, value);
    return;
  }
  fallbackStorage.set(key, value);
};

const deleteStoredString = (key: string): void => {
  if (typeof storage.delete === 'function') {
    storage.delete(key);
    return;
  }
  fallbackStorage.delete(key);
};

const setStoredPrompt = (prompt: AppUpdatePrompt | null, pendingCompletion = false): void => {
  if (!prompt) {
    deleteStoredString(STORAGE_KEY);
    return;
  }
  const scopeKey = getStorageScopeKey();
  if (!scopeKey) return;
  setStoredString(STORAGE_KEY, JSON.stringify({
    scopeKey,
    campaignId: prompt.campaignId,
    snoozeUntil: prompt.snoozeUntil,
    pendingCompletion: pendingCompletion ? prompt : null,
  } satisfies StoredPrompt));
};

const recordUpdatePromptLog = (
  level: 'info' | 'warn' | 'error',
  action: string,
  prompt?: Pick<AppUpdatePrompt, 'campaignId' | 'platform' | 'targetBuild'> | null,
  data?: Record<string, unknown>
): void => {
  const payload = {
    campaign_id: prompt?.campaignId ?? null,
    platform: prompt?.platform ?? normalizePlatform(),
    client_build: getRuntimeClientBuild(),
    action,
    ...data,
  };
  recordBufferedLogEvent(level, 'app_update_prompt', action, payload);
  addSentryBreadcrumb({
    category: 'app_update_prompt',
    message: action,
    level: level === 'error' ? 'error' : level === 'warn' ? 'warning' : 'info',
    data: payload,
  });
  // logger.error dispara captureSentryLoggerError e duplicaria a issue já
  // enviada por captureSentryExceptionWithScope neste módulo.
  if (level === 'error' || level === 'warn') {
    logger.warn(`[UPDATE-PROMPT] ${action}`, payload);
  } else {
    logger.info(`[UPDATE-PROMPT] ${action}`, payload);
  }
};

const sanitizeErrorDiagnosticText = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const normalized = value
    .replace(/bearer\s+[^\s,;]+/gi, 'Bearer [redacted]')
    .replace(/https?:\/\/[^\s,;]+/gi, '[url]')
    .replace(/eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g, '[token]')
    .replace(/\s+/g, ' ')
    .trim();
  if (!normalized) return null;
  return normalized.slice(0, ERROR_DIAGNOSTIC_TEXT_LIMIT);
};

const getUpdatePromptErrorDiagnostic = (error: unknown): UpdatePromptErrorDiagnostic => {
  const rpcError = error && typeof error === 'object'
    ? error as UpdatePromptRpcError
    : null;
  return {
    error_code: sanitizeErrorDiagnosticText(rpcError?.code),
    error_message: sanitizeErrorDiagnosticText(
      error instanceof Error ? error.message : rpcError?.message,
    ) ?? 'update_prompt_error',
    error_details: sanitizeErrorDiagnosticText(rpcError?.details),
    error_hint: sanitizeErrorDiagnosticText(rpcError?.hint),
  };
};

const collectUpdatePromptErrorHaystack = (error: unknown, depth = 0): string => {
  if (error == null || depth > CONNECTIVITY_ERROR_HAYSTACK_DEPTH) return '';
  const parts: string[] = [];
  const push = (value: unknown): void => {
    if (typeof value === 'string' && value.trim()) parts.push(value);
  };
  if (typeof error === 'string') {
    push(error);
    return parts.join(' ');
  }
  if (error instanceof Error) {
    push(error.name);
    push(error.message);
    push(error.stack);
    const cause = (error as Error & { cause?: unknown }).cause;
    if (cause) parts.push(collectUpdatePromptErrorHaystack(cause, depth + 1));
  }
  if (error && typeof error === 'object') {
    const maybeError = error as UpdatePromptRpcError & { name?: unknown; stack?: unknown };
    push(maybeError.name);
    push(maybeError.code);
    push(maybeError.message);
    push(maybeError.details);
    push(maybeError.hint);
    push(maybeError.stack);
  }
  return parts.join(' ');
};

const isUpdatePromptConnectivityFailure = (error: unknown): boolean => {
  if (!globalState.network.isConnected.get()) return true;
  const haystack = collectUpdatePromptErrorHaystack(error).toLowerCase();
  if (!haystack) return false;
  return UPDATE_PROMPT_CONNECTIVITY_HINTS.some(hint => haystack.includes(hint));
};

const reportUpdatePromptError = (
  error: unknown,
  action: string,
  prompt?: AppUpdatePrompt | null
): void => {
  const platform = prompt?.platform ?? normalizePlatform();
  const clientBuild = getRuntimeClientBuild();
  const campaignId = prompt?.campaignId ?? null;
  const diagnostic = getUpdatePromptErrorDiagnostic(error);
  const deviceOnline = Boolean(globalState.network.isConnected.get());

  if (isUpdatePromptConnectivityFailure(error)) {
    recordUpdatePromptLog('warn', 'connectivity_unavailable', prompt, {
      failure_action: action,
      reason: 'connectivity_unavailable',
      device_online: deviceOnline,
      ...diagnostic,
    });
    return;
  }

  recordUpdatePromptLog('error', 'error', prompt, {
    failure_action: action,
    reason: 'unexpected_online_failure',
    device_online: deviceOnline,
    ...diagnostic,
  });
  captureSentryExceptionWithScope(error, {
    level: 'error',
    tags: {
      feature: 'update_prompts_v1',
      campaign_id: campaignId ?? 'none',
      platform: platform ?? 'unknown',
      client_build: clientBuild,
      action,
    },
    context: {
      campaign_id: campaignId,
      platform,
      client_build: clientBuild,
      action,
      device_online: deviceOnline,
      ...diagnostic,
    },
    dedupeKey: `update_prompt_${action}:${platform ?? 'unknown'}:${campaignId ?? 'none'}`,
    dedupeWindowMs: 5 * 60 * 1000,
  });
};

const isInstallationMissingError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;
  const maybeError = error as UpdatePromptRpcError;
  return maybeError.code === '42501' && maybeError.message === 'update_prompt_installation_missing';
};

const normalizePrompt = (raw: RawUpdatePrompt | null): AppUpdatePrompt | null => {
  if (!raw || typeof raw !== 'object') return null;
  if (
    typeof raw.campaign_id !== 'string'
    || (raw.mode !== 'optional' && raw.mode !== 'required')
    || (raw.platform !== 'web' && raw.platform !== 'android' && raw.platform !== 'ios')
    || typeof raw.target_build !== 'number'
    || !Number.isInteger(raw.target_build)
    || raw.target_build <= 0
    || typeof raw.title !== 'string'
    || typeof raw.message !== 'string'
  ) {
    return null;
  }

  return {
    campaignId: raw.campaign_id,
    mode: raw.mode,
    platform: raw.platform,
    targetBuild: raw.target_build,
    targetVersion: typeof raw.target_version === 'string' ? raw.target_version : null,
    title: raw.title,
    message: raw.message,
    storeUrl: typeof raw.store_url === 'string' ? raw.store_url : null,
    webUrl: typeof raw.web_url === 'string' ? raw.web_url : null,
    expiresAt: typeof raw.expires_at === 'string' ? raw.expires_at : null,
    shownCount: typeof raw.shown_count === 'number' ? raw.shown_count : 0,
    snoozeCount: typeof raw.snooze_count === 'number' ? raw.snooze_count : 0,
    snoozeUntil: typeof raw.snooze_until === 'string' ? raw.snooze_until : null,
    completedAt: typeof raw.completed_at === 'string' ? raw.completed_at : null,
  };
};

const isIdentityReady = (): boolean => {
  const user = globalState.user.user.get();
  const profile = globalState.user.profile.get();
  const company = globalState.company.data.get();
  return Boolean(
    globalState.network.isConnected.get()
    && user?.id
    && profile?.id === user.id
    && profile.empresa_id
    && company?.id === profile.empresa_id
    && normalizePlatform()
    && getRuntimeClientBuild() > 0
  );
};

const hasUpdateDestination = (prompt: AppUpdatePrompt): boolean => {
  if (prompt.platform === 'web') return true;
  const configuredUrl = prompt.storeUrl || (
    prompt.platform === 'android'
      ? APP_CONSTANTS.STORE_URLS?.ANDROID_MARKET_URL
      : APP_CONSTANTS.STORE_URLS?.IOS_APP_STORE_URL
  );
  if (!configuredUrl) return false;
  if (prompt.platform === 'android') {
    return /^https:\/\/play\.google\.com\//i.test(configuredUrl)
      || /^market:\/\//i.test(configuredUrl);
  }
  return /^https:\/\/apps\.apple\.com\//i.test(configuredUrl)
    || /^itms-apps:\/\//i.test(configuredUrl);
};

const recordEvent = async (
  prompt: AppUpdatePrompt,
  action: AppUpdatePromptAction,
  snoozeUntil?: string | null
): Promise<boolean> => {
  const platform = normalizePlatform();
  const scopeKey = getStorageScopeKey();
  if (!platform || !scopeKey || prompt.platform !== platform
    || (promptScopes.has(prompt) && promptScopes.get(prompt) !== scopeKey)) return false;
  try {
    const { data, error } = await supabase.rpc('app_guincho_record_update_prompt_event_v1', {
      p_campaign_id: prompt.campaignId,
      p_device_id: getStableRecoveryDeviceId(),
      p_platform: platform,
      p_client_build: getRuntimeClientBuild(),
      p_action: action,
      p_snooze_until: snoozeUntil ?? null,
    });
    if (error) throw error;
    if (scopeKey !== getStorageScopeKey()) return false;
    if (!data || typeof data !== 'object' || data.campaign_id !== prompt.campaignId) return false;
    if (action === 'completed') return typeof data.completed_at === 'string' && Number.isFinite(Date.parse(data.completed_at));
    return true;
  } catch (error) {
    reportUpdatePromptError(error, action, prompt);
    return false;
  }
};

const recordEventWithinBudget = async (
  prompt: AppUpdatePrompt,
  action: AppUpdatePromptAction
): Promise<void> => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([
      recordEvent(prompt, action).then(() => undefined),
      new Promise<void>(resolve => {
        timeoutId = setTimeout(resolve, EVENT_FLUSH_BUDGET_MS);
      }),
    ]);
  } finally {
    if (timeoutId !== undefined) clearTimeout(timeoutId);
  }
};

const clearPrompt = (action: string, prompt: AppUpdatePrompt | null = snapshot.prompt): void => {
  setStoredPrompt(null);
  emit({ prompt: null });
  recordUpdatePromptLog('info', action, prompt);
};

const checkOnce = async (force = false, requestedScope = getStorageScopeKey()): Promise<AppUpdatePrompt | null> => {
  const scopeKey = getStorageScopeKey();
  if (requestedScope !== scopeKey) return null;
  const clientBuild = getAppUpdateCampaignBuild();
  if (scopeKey !== lastScopeKey) {
    emit({ prompt: null });
    lastScopeKey = scopeKey;
    lastCheckedAt = null;
  }
  const storedBefore = parseStoredPrompt(getStoredString(STORAGE_KEY));
  if (storedBefore && storedBefore.scopeKey !== scopeKey) deleteStoredString(STORAGE_KEY);
  const currentPrompt = snapshot.prompt ?? (storedBefore?.scopeKey === scopeKey
    && storedBefore.pendingCompletion?.platform === normalizePlatform() ? storedBefore.pendingCompletion : null);
  if (currentPrompt && clientBuild !== null && clientBuild >= currentPrompt.targetBuild) {
    setStoredPrompt(currentPrompt, true);
    emit({ prompt: null });
  }
  if (!isIdentityReady() || !scopeKey) {
    emit({ prompt: null, isChecking: false });
    lastCheckedAt = null;
    return null;
  }
  const now = Date.now();
  if (!force && lastCheckedBuild === clientBuild && lastCheckedAt !== null && now - lastCheckedAt < CHECK_THROTTLE_MS) {
    return snapshot.prompt;
  }
  lastCheckedAt = now;
  lastCheckedBuild = clientBuild;
  const platform = normalizePlatform();
  if (!platform) return null;
  const isCurrentScope = (): boolean => scopeKey === getStorageScopeKey();
  const confirmCompletion = async (prompt: AppUpdatePrompt): Promise<void> => {
    setStoredPrompt(prompt, true);
    emit({ prompt: null });
    const confirmed = await recordEvent(prompt, 'completed');
    if (!isCurrentScope()) return;
    if (confirmed) clearPrompt('completed', prompt);
    else recordUpdatePromptLog('info', 'completion_pending', prompt);
  };

  emit({ isChecking: true, lastError: null });
  try {
    await reportClientInstallationPresence().catch(() => false);
    if (!isCurrentScope()) return null;
    if (clientBuild === null) {
      emit({ prompt: null });
      recordUpdatePromptLog('info', 'campaign_build_unknown', null, { campaign_build: null });
      return null;
    }
    if (currentPrompt && currentPrompt.platform === platform && clientBuild !== null && clientBuild >= currentPrompt.targetBuild) {
      await confirmCompletion(currentPrompt);
      if (!isCurrentScope()) return null;
    }
    const { data, error } = await supabase.rpc('app_guincho_resolve_update_prompt_v1', {
      p_device_id: getStableRecoveryDeviceId(),
      p_platform: platform,
      p_client_build: getRuntimeClientBuild(),
    });
    if (!isCurrentScope()) return null;
    if (error) throw error;
    const prompt = normalizePrompt(data as RawUpdatePrompt | null);
    if (!prompt) {
      const stored = parseStoredPrompt(getStoredString(STORAGE_KEY));
      if (
        stored?.scopeKey === scopeKey
        && stored.snoozeUntil
        && Date.parse(stored.snoozeUntil) > now
      ) {
        emit({ prompt: null, isChecking: false, lastError: null });
        recordUpdatePromptLog('info', 'resolved', null, {
          campaign_id: stored.campaignId,
          snoozed: true,
          snooze_until: stored.snoozeUntil,
        });
        return null;
      }
      if (snapshot.prompt) clearPrompt('cancelled', snapshot.prompt);
      setStoredPrompt(null);
      emit({ prompt: null, isChecking: false });
      return null;
    }

    if (prompt.expiresAt && Date.parse(prompt.expiresAt) <= now) {
      clearPrompt('cancelled', prompt);
      emit({ isChecking: false });
      return null;
    }

    if (!hasUpdateDestination(prompt)) {
      reportUpdatePromptError(new Error('update_prompt_store_destination_missing'), 'resolve', prompt);
      clearPrompt('cancelled', prompt);
      emit({ isChecking: false });
      return null;
    }

    if (prompt.platform !== platform || clientBuild === null) {
      emit({ prompt: null });
      recordUpdatePromptLog('info', 'campaign_build_unknown', prompt);
      return null;
    }
    if (clientBuild >= prompt.targetBuild) {
      if (currentPrompt?.campaignId !== prompt.campaignId) await confirmCompletion(prompt);
      return null;
    }

    const stored = parseStoredPrompt(getStoredString(STORAGE_KEY));
    if (stored && stored.scopeKey !== scopeKey) deleteStoredString(STORAGE_KEY);

    const sameCampaign = stored?.scopeKey === scopeKey && stored.campaignId === prompt.campaignId;
    const effectiveSnoozeUntil = prompt.mode === 'required'
      ? null
      : prompt.snoozeUntil ?? (sameCampaign ? stored?.snoozeUntil ?? null : null);
    const resolvedPrompt = { ...prompt, snoozeUntil: effectiveSnoozeUntil };
    promptScopes.set(resolvedPrompt, scopeKey);
    setStoredPrompt(resolvedPrompt);
    if (prompt.mode === 'optional' && effectiveSnoozeUntil && Date.parse(effectiveSnoozeUntil) > now) {
      emit({ prompt: null, isChecking: false, lastError: null });
      recordUpdatePromptLog('info', 'resolved', resolvedPrompt, {
        mode: prompt.mode,
        snoozed: true,
        snooze_until: effectiveSnoozeUntil,
      });
      return null;
    }

    emit({ prompt: resolvedPrompt, isChecking: false, lastError: null });
    recordUpdatePromptLog('info', 'resolved', resolvedPrompt, {
      mode: prompt.mode,
      snooze_count: prompt.snoozeCount,
    });
    return resolvedPrompt;
  } catch (error) {
    if (!isCurrentScope()) return null;
    emit({ prompt: null, lastError: isInstallationMissingError(error) ? null : error instanceof Error ? error.message : 'update_prompt_error' });
    if (isInstallationMissingError(error)) recordUpdatePromptLog('info', 'installation_pending', null, { reason: 'client_installation_presence_missing' });
    else reportUpdatePromptError(error, 'resolve', currentPrompt);
    return null;
  } finally {
    if (isCurrentScope()) emit({ isChecking: false });
    else emit({ prompt: null, isChecking: false });
  }
};

export const subscribeAppUpdatePrompt = (
  listener: (next: UpdatePromptSnapshot) => void
): (() => void) => {
  listeners.add(listener);
  listener(snapshot);
  return () => {
    listeners.delete(listener);
  };
};

export const getAppUpdatePromptSnapshot = (): UpdatePromptSnapshot => snapshot;

export const checkAppUpdatePrompt = (force = false): Promise<AppUpdatePrompt | null> => {
  const requestedScope = getStorageScopeKey();
  if (requestedScope !== lastScopeKey) emit({ prompt: null, isChecking: false });
  const next = checkQueue.catch(() => null).then(() => checkOnce(force, requestedScope));
  checkQueue = next;
  return next;
};

export const checkAppUpdatePromptBestEffort = (force = false): void => {
  void checkAppUpdatePrompt(force).catch(() => undefined);
};

export const markAppUpdatePromptShown = async (prompt: AppUpdatePrompt): Promise<void> => {
  await recordEvent(prompt, 'shown');
  recordUpdatePromptLog('info', 'shown', prompt, { mode: prompt.mode });
};

export const snoozeAppUpdatePrompt = async (prompt: AppUpdatePrompt): Promise<void> => {
  if (prompt.mode === 'required' || promptScopes.get(prompt) !== getStorageScopeKey()) return;
  const snoozeUntil = new Date(Date.now() + OPTIONAL_SNOOZE_MS).toISOString();
  const nextPrompt = { ...prompt, snoozeUntil, snoozeCount: prompt.snoozeCount + 1 };
  setStoredPrompt(nextPrompt);
  emit({ prompt: null });
  recordUpdatePromptLog('info', 'snoozed', nextPrompt, { snooze_until: snoozeUntil });
  void recordEvent(prompt, 'snoozed', snoozeUntil);
};

const resolveStoreUrl = (prompt: AppUpdatePrompt): string => {
  if (prompt.platform === 'android') {
    return prompt.storeUrl || APP_CONSTANTS.STORE_URLS?.ANDROID_MARKET_URL || '';
  }
  if (prompt.platform === 'ios') {
    return prompt.storeUrl || APP_CONSTANTS.STORE_URLS?.IOS_APP_STORE_URL || '';
  }
  return prompt.webUrl || APP_CONSTANTS.URLS.PUBLIC_URL;
};

export const openAppUpdateDestination = async (prompt: AppUpdatePrompt): Promise<void> => {
  const scopeKey = getStorageScopeKey();
  if (promptScopes.has(prompt) && promptScopes.get(prompt) !== scopeKey) return;
  if (prompt.platform === 'web') {
    recordUpdatePromptLog('info', 'web_refresh', prompt);
    try {
      await recordEventWithinBudget(prompt, 'web_refresh');
      if (scopeKey !== getStorageScopeKey()) return;
      if (typeof window !== 'undefined') {
        if (prompt.mode === 'optional') {
          setStoredPrompt({
            ...prompt,
            snoozeUntil: new Date(Date.now() + WEB_REFRESH_SNOOZE_MS).toISOString(),
          });
        }
        const url = new URL(window.location.href);
        url.searchParams.set('app_update_refresh', `${prompt.campaignId}-${Date.now()}`);
        window.location.replace(url.toString());
      }
    } catch (error) {
      reportUpdatePromptError(error, 'web_refresh', prompt);
    }
    return;
  }

  const url = resolveStoreUrl(prompt);
  if (!url || !hasUpdateDestination(prompt)) {
    reportUpdatePromptError(new Error('update_prompt_store_destination_missing'), 'open', prompt);
    return;
  }
  void recordEvent(prompt, 'store_opened');
  try {
    await Linking.openURL(url);
    recordUpdatePromptLog('info', 'opened', prompt, { destination: prompt.platform });
  } catch (error) {
    const androidWebUrl = APP_CONSTANTS.STORE_URLS?.ANDROID_WEB_URL;
    if (prompt.platform === 'android' && androidWebUrl && url !== androidWebUrl) {
      try {
        await Linking.openURL(androidWebUrl);
        recordUpdatePromptLog('info', 'opened', prompt, { destination: 'android_web_fallback' });
        return;
      } catch (fallbackError) {
        reportUpdatePromptError(fallbackError, 'open', prompt);
        return;
      }
    }
    reportUpdatePromptError(error, 'open', prompt);
  }
};
